import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { IPosition } from '@medely/types';
import type { PerDiemFilterEventMetadata as EventMetadata } from '@mp';
import { useDistance, DEFAULT_DISTANCE } from 'hooks/findJobsFilters/useDistance';
import { useMinimumPayRate } from 'hooks/findJobsFilters/useMinimumPayRate';
import { useSchedule } from 'hooks/findJobsFilters/useSchedule';
import { useShiftLength } from 'hooks/findJobsFilters/useShiftLength';
import { useShiftType } from 'hooks/findJobsFilters/useShiftType';
import { useStartAfterDate } from 'hooks/findJobsFilters/useStartAfterDate';
import { usePositionIds } from 'hooks/findJobsFilters/usePositionIds';
import useSessionLocalStorage from 'hooks/useSessionLocalStorage';

interface IContext {
  clearFilters: () => void;
  distance: number;
  eventMetadata?: EventMetadata;
  filters?: {
    daily_rate_cents: number;
    position_ids?: IPosition['id'][];
    schedule: string[];
    shift_length?: { min?: number; max?: number };
    starts_date: string;
  };
  hasActiveFilters: boolean;
  isLoading: boolean;
  selectedFiltersCount: number;
  shiftType?: string[];
}

const PerDiemFiltersContext = createContext<IContext>({
  clearFilters: () => null,
  distance: DEFAULT_DISTANCE,
  eventMetadata: undefined,
  filters: undefined,
  hasActiveFilters: false,
  isLoading: true,
  selectedFiltersCount: 0,
  shiftType: undefined,
});

const STORAGE_KEY = 'per-diem-filters';

type PerDiemStorageFilters = {
  distance: number;
  minimumRate: number;
  positionIds?: IPosition['id'][];
  schedule: string[];
  shiftLength?: number[];
  shiftType: string[];
};

const useClearFilters = () => {
  const { clearMinimumRate } = useMinimumPayRate();
  const { clearSchedule } = useSchedule();
  const { clearSelectedPositionIds } = usePositionIds();
  const { clearShiftLength } = useShiftLength();
  const { clearShiftType } = useShiftType();

  const clearFilters = () => {
    clearMinimumRate();
    clearSchedule();
    clearSelectedPositionIds();
    clearShiftLength();
    clearShiftType();
  };

  return { clearFilters };
};

const useActiveFilterMetadata = () => {
  const { hasMinimumRate } = useMinimumPayRate();
  const { hasSchedule } = useSchedule();
  const { hasSelectedPositions } = usePositionIds();
  const { hasShiftLength } = useShiftLength();
  const { hasShiftType } = useShiftType();

  const selectedFiltersCount = [
    hasMinimumRate,
    hasSchedule,
    hasSelectedPositions,
    hasShiftLength,
    hasShiftType,
  ].filter((filter) => filter).length;

  const hasActiveFilters = !!selectedFiltersCount;

  return { hasActiveFilters, selectedFiltersCount };
};

const useEventMetadata = () => {
  const { minimumRate: minimum_rate } = useMinimumPayRate();
  const { positionIds: position_ids } = usePositionIds();
  const { schedule } = useSchedule();
  const { hasShiftLength, min: minShiftLength, max: maxShiftLength } = useShiftLength();
  const shift_length = hasShiftLength ? { minShiftLength, maxShiftLength } : {};
  const { shiftType: shift_type } = useShiftType();

  return {
    eventMetadata: {
      minimum_rate,
      position_ids: position_ids ?? [],
      schedule,
      shift_length,
      shift_type,
    },
  };
};

const useStorageFilters = () => {
  const {
    isLoading: isLoadingStorage,
    value: storageData,
    setValue: setToStorage,
  } = useSessionLocalStorage<PerDiemStorageFilters | undefined>(STORAGE_KEY, undefined);
  const [isLoadingFromStorage, setIsLoadingFromStorage] = useState(true);
  const isLoading = isLoadingFromStorage || isLoadingStorage;
  const { distance, setDistance } = useDistance();

  const { minimumRate, setMinimumRate } = useMinimumPayRate();
  const { positionIds, setPositionIds } = usePositionIds();
  const { schedule, setSchedule } = useSchedule();
  const { min, max, setShiftLength, hasShiftLength } = useShiftLength();
  const shiftLength = hasShiftLength ? [min, max] : undefined;
  const { shiftType, setShiftType } = useShiftType();

  const currentFilters = {
    distance,
    minimumRate,
    positionIds,
    schedule,
    shiftLength,
    shiftType,
  };

  const shouldUpdateStorageData =
    JSON.stringify(storageData) !== JSON.stringify(currentFilters) && !isLoading;

  const updateStorageData = () => {
    setToStorage(currentFilters);
  };

  useEffect(() => {
    const hasNoStorageData = !isLoadingStorage && !storageData;

    if (hasNoStorageData || JSON.stringify(storageData) === JSON.stringify(currentFilters)) {
      setIsLoadingFromStorage(false);
    }
  }, [storageData, currentFilters]);

  useEffect(() => {
    if (!isLoadingStorage && storageData) {
      setDistance(storageData.distance);
      setMinimumRate(storageData.minimumRate);
      setPositionIds(storageData.positionIds as IPosition['id'][]);
      setSchedule(storageData.schedule);
      setShiftType(storageData.shiftType);
      setShiftLength(storageData.shiftLength as number[]);
    }
  }, [isLoadingStorage]);

  useEffect(() => {
    shouldUpdateStorageData && updateStorageData();
  }, [shouldUpdateStorageData]);

  return { isLoading, shouldUpdateStorageData, updateStorageData };
};

export const PerDiemFiltersProvider = ({
  children,
}: {
  children: ReactNode;
}): React.ReactElement => {
  const { distance } = useDistance();
  const { isLoading } = useStorageFilters();

  const { hasActiveFilters, selectedFiltersCount } = useActiveFilterMetadata();
  const { clearFilters } = useClearFilters();
  const { eventMetadata } = useEventMetadata();

  const { minimumRate: daily_rate_cents } = useMinimumPayRate();
  const { positionIds: position_ids } = usePositionIds();
  const { schedule } = useSchedule();
  const { min, max, hasShiftLength } = useShiftLength();
  const { shiftType } = useShiftType();
  const { startsDate: starts_date } = useStartAfterDate();

  return (
    <PerDiemFiltersContext.Provider
      value={{
        distance,
        clearFilters,
        eventMetadata,
        filters: {
          daily_rate_cents,
          position_ids,
          schedule,
          shift_length: hasShiftLength ? { min, max } : undefined,
          starts_date,
        },
        hasActiveFilters,
        isLoading,
        selectedFiltersCount,
        shiftType,
      }}
    >
      {children}
    </PerDiemFiltersContext.Provider>
  );
};

export const usePerDiemFiltersContext = (): IContext => useContext(PerDiemFiltersContext);
